import { dropTask } from 'ember-concurrency';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import * as v from 'valibot';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type Owner from '@ember/owner';
import errorsForField from 'tio-common/helpers/errors-for-field';
import RadioGroup from 'tio-ui/components/radio-group';
import Input from 'tio-ui/components/input';
import Select from 'tio-ui/components/select';
import Checkbox from 'tio-ui/components/checkbox';
import type { Store } from '@warp-drive/core';
import { saveRecord } from 'tio-data/requests/save-record';
import t from 'ember-intl/helpers/t';
import AlertBanner from 'tio-ui/components/alert-banner';
import Popover from 'tio-ui/components/popover';
import type { IntlService } from 'ember-intl';
import type { LoanSummary } from 'tio-common/types/loan-summary';
import type { RecommendationExtension as RecommendationModel } from 'tio-data/data/resources/recommendation.ext';
import type { UserExtension as UserModel } from 'tio-data/data/resources/user.ext';
import Button from 'tio-ui/components/button';
import inputmask from 'tio-common/modifiers/inputmask';
import { getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import {
  answerProfilePrefill,
  answerProfileUpdates,
  buildSortPlansPayloads,
  CURRENT_PLAN_OPTION_KEYS,
  loanInputsFromSummary,
  missingForgivenessSubQuestions,
  REQUESTING_APP,
  sf0726FormSchema,
  STATE_OPTIONS,
  type Sf0726FormModel,
  type Sf0726LoanInput,
  type Sf0726NsldsPrefill,
} from '../../utils/strategy-finder-0726';

// Federal Student Aid (U.S. Dept. of Education) Teacher Loan Forgiveness page.
const TLF_DOE_URL = 'https://studentaid.gov/manage-loans/forgiveness-cancellation/teacher';

// Editable per-loan card state. Tracked so edits in the inputs/dropdown
// re-render and flow into the submitted payload.
class LoanInputRow {
  loanType: string;
  loanCount: number;
  @tracked currentBalanceDollars: number;
  @tracked interestRatePercent: number;
  @tracked monthlyPaymentDollars: number;
  @tracked originalDisbursementDollars: number;
  @tracked repaymentPlan: string;

  constructor(seed: Sf0726LoanInput) {
    this.loanType = seed.loanType;
    this.loanCount = seed.loanCount;
    this.currentBalanceDollars = seed.currentBalanceDollars;
    this.interestRatePercent = seed.interestRatePercent;
    this.monthlyPaymentDollars = seed.monthlyPaymentDollars;
    this.originalDisbursementDollars = seed.originalDisbursementDollars;
    this.repaymentPlan = seed.repaymentPlan;
  }
}

interface StrategyFinder0726FormSignature {
  Args: {
    user: UserModel;
    loanSummary: LoanSummary;
    nsldsPrefill?: Sf0726NsldsPrefill | null;
    didSave: (recommendation: RecommendationModel) => void;
    // Optional, host-app specific: persist the entered answers. The employee app
    // writes them to its shared answer profile; the agent app has no profile and
    // omits this. The return value is ignored (the form just awaits it).
    onSaveAnswers?: (updates: ReturnType<typeof answerProfileUpdates>) => unknown;
    // Host app's NSLDS-upload route (employee only). When omitted — e.g. the agent
    // app, which has no NSLDS upload flow — the NSLDS-upload prompts are hidden.
    nsldsUploadRoute?: string;
  };
  Element: HTMLElement;
}

export default class StrategyFinder0726FormComponent extends Component<StrategyFinder0726FormSignature> {
  @service declare store: Store;
  @service declare intl: IntlService;

  @tracked payoffGoal = '';
  @tracked affordPayments = '';
  @tracked agiDollars = 0;
  @tracked adultsChoice = '';
  @tracked numberOfChildren = 0;
  @tracked firstLoanTime = '';
  @tracked lastLoanTime = '';
  @tracked stateOfResidence = '';
  // Loan-forgiveness flow (mirrors the existing Repayment Strategy Finder).
  @tracked currentlyWorkingToward = '';
  @tracked currentForgivenessProgram = '';
  @tracked madePayments = 0;
  @tracked isGov = false;
  @tracked isTeacher = false;
  @tracked isOther = false;
  @tracked planInGov = '';
  @tracked planAsTeacher = '';
  @tracked employmentMonths = 0;
  @tracked tlfQualifies17500 = false;
  @tracked hasSubmitted = false;
  @tracked showIncompleteAlert = false;
  @tracked submitError: unknown = null;

  loanRows: LoanInputRow[];

  constructor(owner: Owner, args: StrategyFinder0726FormSignature['Args']) {
    super(owner, args);

    this.loanRows = loanInputsFromSummary(args.loanSummary).map((seed) => new LoanInputRow(seed));

    // Pre-fill every answer from the shared answer profile (the user's latest
    // answers from anywhere in the app), for both first-time and repeat users.
    const prefill = answerProfilePrefill(args.user?.answerProfile);
    this.payoffGoal = prefill.payoffGoal;
    this.affordPayments = prefill.affordPayments;
    this.agiDollars = prefill.agiDollars;
    this.adultsChoice = prefill.adultsChoice;
    this.numberOfChildren = prefill.numberOfChildren;
    this.firstLoanTime = prefill.firstLoanTime;
    this.lastLoanTime = prefill.lastLoanTime;
    this.stateOfResidence = prefill.residenceState;
    this.currentlyWorkingToward = prefill.currentlyWorkingToward;
    this.currentForgivenessProgram = prefill.currentForgivenessProgram;
    this.madePayments = prefill.madePayments;
    this.isGov = prefill.isGov;
    this.isTeacher = prefill.isTeacher;
    this.isOther = prefill.isOther;
    this.planInGov = prefill.planInGov;
    this.planAsTeacher = prefill.planAsTeacher;
    this.employmentMonths = prefill.employmentMonths;
    this.tlfQualifies17500 = prefill.tlfQualifies17500;

    // NSLDS-derived answers take precedence over the answer profile — the wellness
    // train computed these from the user's uploaded NSLDS file. The user can still
    // change anything; an explanatory note sits above each affected question.
    const nslds = args.nsldsPrefill;
    if (nslds?.firstLoanTime) {
      this.firstLoanTime = nslds.firstLoanTime;
    }
    if (nslds?.lastLoanTime) {
      this.lastLoanTime = nslds.lastLoanTime;
    }
    if (nslds?.pslfCount != null) {
      this.currentlyWorkingToward = 'yes';
      this.currentForgivenessProgram = 'PublicServiceLoanForgiveness';
      this.isGov = true;
      this.isOther = false; // government and "other / private sector" are mutually exclusive
      this.madePayments = nslds.pslfCount;
      this.employmentMonths = nslds.pslfCount;
      this.planInGov = 'for-10-years-more'; // PSLF-qualifying payments imply the 10-year path
    }
  }

  get states() {
    return STATE_OPTIONS.map((state) => ({
      label: state.name,
      key: state.abbr,
    }));
  }

  get planOptions() {
    return CURRENT_PLAN_OPTION_KEYS.map((key) => ({
      label: this.intl.t(`strategy_finder_0726.plans.${key}`),
      key,
    }));
  }

  get formModel(): Sf0726FormModel {
    return {
      payoffGoal: this.payoffGoal,
      affordPayments: this.affordPayments,
      agi: Math.round(this.agiDollars * 100),
      numberOfAdults: this.adultsChoice ? parseInt(this.adultsChoice, 10) : 0,
      numberOfChildren: this.numberOfChildren,
      firstLoanTime: this.firstLoanTime,
      lastLoanTime: this.lastLoanTime,
      residenceState: this.stateOfResidence,
      currentlyWorkingToward: this.currentlyWorkingToward,
      currentForgivenessProgram: this.currentForgivenessProgram,
      madePayments: this.madePayments,
      isGov: this.isGov,
      isTeacher: this.isTeacher,
      isOther: this.isOther,
      planInGov: this.planInGov,
      planAsTeacher: this.planAsTeacher,
      employmentMonths: this.employmentMonths,
      tlfQualifies17500: this.tlfQualifies17500,
    };
  }

  // Forgiveness program options for the "which program" select.
  get programOptions() {
    return [
      'PublicServiceLoanForgiveness',
      'TeacherLoanForgiveness',
      'IncomeDrivenLoanForgiveness',
    ].map((key) => ({
      label: this.intl.t(`strategy_finder_0726.forgiveness_program.${key}`),
      key,
    }));
  }

  // The program select + "qualifying payments" show only when actively pursuing;
  // the employment block also shows for "not yet, but want to learn" so we can
  // surface eligibility without marking them as pursuing.
  get showWorkingDetails() {
    return this.currentlyWorkingToward === 'yes';
  }

  get showEmployment() {
    return (
      this.currentlyWorkingToward === 'yes' || this.currentlyWorkingToward === 'no-but-want-learn'
    );
  }

  // Notes that explain the NSLDS-derived pre-fills (and offer a re-upload link).
  // Shown only when the wellness train actually derived those answers.
  get showCohortNote() {
    const prefill = this.args.nsldsPrefill;
    // Require BOTH cohorts: the note states we chose both loan-timing answers, so
    // only show it when both were actually derived (they come from the same NSLDS
    // loan history; the helper defensively allows just one).
    return !!prefill && !!prefill.firstLoanTime && !!prefill.lastLoanTime;
  }

  get showPslfNote() {
    return this.args.nsldsPrefill?.pslfCount != null;
  }

  // Raw PSLF qualifying-payment count, passed to the note's ICU `count` arg.
  get pslfCount() {
    return this.args.nsldsPrefill?.pslfCount ?? 0;
  }

  // Format an NSLDS date (ISO string) for display. UTC keeps a YYYY-MM-DD value
  // from shifting a day in the user's local time zone.
  #formatNsldsDate(value: string | null | undefined) {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    return new Intl.DateTimeFormat(this.intl.primaryLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(date);
  }

  get nsldsGeneratedDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.nsldsAsOf);
  }

  get nsldsFirstLoanDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.firstLoanDate);
  }

  get nsldsLastLoanDateLabel() {
    return this.#formatNsldsDate(this.args.nsldsPrefill?.lastLoanDate);
  }

  setProgram = (value: string) => {
    this.currentForgivenessProgram = value;
  };

  setMadePayments = (value: string) => {
    this.madePayments = Math.trunc(this.toNumber(value));
  };

  setEmploymentMonths = (value: string) => {
    this.employmentMonths = Math.trunc(this.toNumber(value));
  };

  // "Other / Private Sector" is mutually exclusive with the public-service
  // options: picking a public-service role (government or teacher) clears
  // "Other", and picking "Other" clears both. Government and teacher can still
  // be selected together (e.g. a teacher at a public school).
  toggleGov = (checked: boolean) => {
    this.isGov = checked;
    if (checked) {
      this.isOther = false;
    }
  };

  toggleTeacher = (checked: boolean) => {
    this.isTeacher = checked;
    if (checked) {
      this.isOther = false;
    }
  };

  toggleOther = (checked: boolean) => {
    this.isOther = checked;
    if (checked) {
      this.isGov = false;
      this.isTeacher = false;
    }
  };

  setTlfQualifies17500 = (checked: boolean) => {
    this.tlfQualifies17500 = checked;
  };

  get loanInputs(): Sf0726LoanInput[] {
    return this.loanRows;
  }

  // Current plan is required for every loan; the user must pick it each time.
  get missingCurrentPlan() {
    return this.loanRows.some((row) => !row.repaymentPlan);
  }

  loanPlanErrors = (row: LoanInputRow) => {
    return this.hasSubmitted && !row.repaymentPlan
      ? [this.intl.t('strategy_finder_0726.current_plan_required')]
      : [];
  };

  // The gov/teacher "how long…" sub-questions are required once that employment
  // type is selected; surface the error only after a submit attempt, mirroring
  // loanPlanErrors.
  get govPlanErrors() {
    return this.hasSubmitted && this.isGov && !this.planInGov
      ? [this.intl.t('strategy_finder_0726.forgiveness.selection_required')]
      : [];
  }

  get teacherPlanErrors() {
    return this.hasSubmitted && this.isTeacher && !this.planAsTeacher
      ? [this.intl.t('strategy_finder_0726.forgiveness.selection_required')]
      : [];
  }

  get formValidationErrors() {
    try {
      v.parse(sf0726FormSchema, this.formModel);
    } catch (err) {
      if (err instanceof v.ValiError) {
        return err.issues || [];
      }
      return [];
    }
    return [];
  }

  // Errors shaped for the errors-for-field helper (it matches `path` as a string
  // and reads `errors`). Only surfaced after a submit attempt so the missing
  // fields highlight on "See your results", not on first render.
  get displayErrors() {
    if (!this.hasSubmitted) {
      return [];
    }
    return this.formValidationErrors.map((issue) => ({
      path: String(issue.path?.[0]?.key ?? ''),
      errors: [issue.message],
      value: '',
    }));
  }

  loanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  // Short program name (e.g. "Direct") for the loan-card heading, paired with a
  // loan count like "Direct (3 loans)".
  shortLoanTypeLabel = (loanType: string) => {
    const key = `strategy_finder_0726.loan_types_short.${loanType}`;
    return this.intl.exists(key) ? this.intl.t(key) : loanType;
  };

  toNumber = (value: string) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  setRowPlan = (row: LoanInputRow, value: string) => {
    row.repaymentPlan = value;
  };

  // Read a currency-masked input's unmasked number (dollars) from the inputmask,
  // falling back to a plain parse.
  unmaskedDollars = (value: string, event?: InputEvent) => {
    const unmasked = getInputmaskUnmaskedValue(event);
    const dollars = typeof unmasked === 'number' ? unmasked : Number(unmasked);
    return Number.isFinite(dollars) && unmasked !== '' ? dollars : this.toNumber(value);
  };

  // Editable per-loan figures. The currency fields share the AGI inputmask
  // handling; the rate is a plain percent number.
  setRowBalance = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.currentBalanceDollars = this.unmaskedDollars(value, event);
  };

  setRowPayment = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.monthlyPaymentDollars = this.unmaskedDollars(value, event);
  };

  setRowDisbursed = (row: LoanInputRow, value: string, event?: InputEvent) => {
    row.originalDisbursementDollars = this.unmaskedDollars(value, event);
  };

  setRowRate = (row: LoanInputRow, value: string) => {
    row.interestRatePercent = this.toNumber(value);
  };

  // AGI is entered through a currency-masked input; read the unmasked dollars.
  setAgi = (value: string, event?: InputEvent) => {
    this.agiDollars = this.unmaskedDollars(value, event);
  };

  setNumberOfChildren = (value: string) => {
    this.numberOfChildren = Math.trunc(this.toNumber(value));
  };

  setStateOfResidence = (value: string) => {
    this.stateOfResidence = value;
  };

  async storeAnswersOnProfile(formModel: Sf0726FormModel) {
    // Persisting answers is optional and host-app specific (the employee app
    // writes them to the shared answer profile; the agent app has no profile).
    // Failures here must not block the results redirect.
    if (!this.args.onSaveAnswers) {
      return;
    }
    try {
      await this.args.onSaveAnswers(answerProfileUpdates(formModel));
    } catch (e) {
      console.error(e);
    }
  }

  submitTask = dropTask(async () => {
    this.submitError = null;
    this.hasSubmitted = true;

    // Block on incomplete shared answers, no loans, or any loan missing a
    // current plan (the user must choose one for every loan).
    if (
      this.formValidationErrors.length ||
      this.loanInputs.length === 0 ||
      this.missingCurrentPlan ||
      missingForgivenessSubQuestions(this.formModel)
    ) {
      this.showIncompleteAlert = true;
      return;
    }
    this.showIncompleteAlert = false;

    try {
      const formModel = this.formModel;
      const data = buildSortPlansPayloads(formModel, this.loanInputs);
      const recommendation = this.store.createRecord<RecommendationModel>('recommendation', {
        user: this.args.user,
        calculationInput: { data },
        requestingApp: REQUESTING_APP,
      });
      await saveRecord(this.store, recommendation);
      await this.storeAnswersOnProfile(formModel);
      this.hasSubmitted = false;
      this.args.didSave?.(recommendation);
    } catch (e) {
      this.submitError = e;
      console.error(e);
    }
  });

  <template>
    <form data-test-sf-0726-form ...attributes>
      <div class="pb-4">
        <h2 class="tio-text-md-semibold">{{t "strategy_finder_0726.loans_heading"}}</h2>
        <p class="text-xs mt-1 mb-3">{{t "strategy_finder_0726.loans_blurb"}}</p>
        <div class="grid gap-3 md:grid-cols-2">
          {{#each this.loanRows as |row|}}
            <div class="tio-surface-panel" data-test-sf-0726-loan-card={{row.loanType}}>
              <p class="tio-text-sm-semibold pb-2">
                {{this.shortLoanTypeLabel row.loanType}}
                ({{t "strategy_finder_0726.loan_count" count=row.loanCount}})
              </p>

              {{! Loan figures come from the loan summary but are editable, so the
                  borrower can correct them; the values flow into the results. }}
              <div class="space-y-2 pb-3">
                <div>
                  <label for="balance-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_current_balance"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowBalance row}}
                    @value={{row.currentBalanceDollars}}
                    autocomplete="off"
                    id="balance-{{row.loanType}}"
                    data-test-sf-0726-loan-balance={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
                <div>
                  <label for="rate-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_interest_rate"
                    }}
                    (%)</label>
                  <Input
                    @onChange={{fn this.setRowRate row}}
                    type="number"
                    @value={{row.interestRatePercent}}
                    autocomplete="off"
                    min="0"
                    step="0.01"
                    id="rate-{{row.loanType}}"
                    data-test-sf-0726-loan-rate={{row.loanType}}
                  />
                </div>
                <div>
                  <label for="payment-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_monthly_payment"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowPayment row}}
                    @value={{row.monthlyPaymentDollars}}
                    autocomplete="off"
                    id="payment-{{row.loanType}}"
                    data-test-sf-0726-loan-payment={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
                <div>
                  <label for="disbursed-{{row.loanType}}" class="form-field-label !text-sm">{{t
                      "strategy_finder_0726.loan_disbursed_amount"
                    }}</label>
                  <Input
                    @onChange={{fn this.setRowDisbursed row}}
                    @value={{row.originalDisbursementDollars}}
                    autocomplete="off"
                    id="disbursed-{{row.loanType}}"
                    data-test-sf-0726-loan-disbursed={{row.loanType}}
                    {{inputmask
                      alias="currency"
                      prefix="$"
                      unmaskAsNumber=true
                      digits="0"
                      digitsOptional=false
                    }}
                  />
                </div>
              </div>

              <div>
                <label for="plan-{{row.loanType}}" class="form-field-label !text-sm">{{t
                    "strategy_finder_0726.loan_current_plan"
                  }}</label>
                <Select
                  @onChange={{fn this.setRowPlan row}}
                  @items={{this.planOptions}}
                  @selected={{row.repaymentPlan}}
                  @errors={{this.loanPlanErrors row}}
                  @id="plan-{{row.loanType}}"
                  data-test-sf-0726-loan-plan={{row.loanType}}
                />
              </div>
            </div>
          {{/each}}
        </div>
        {{#if @nsldsUploadRoute}}
          <AlertBanner @intent="info" class="mt-3" data-test-sf-0726-nslds-note>
            <p class="text-sm">
              {{t "strategy_finder_0726.loans_nslds_note"}}
              <LinkTo
                @route={{@nsldsUploadRoute}}
                @query={{hash providers="nslds" source="strategy_finder"}}
                class="tio-copy"
                data-test-sf-0726-nslds-upload-link
              >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
            </p>
          </AlertBanner>
        {{/if}}
      </div>

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "payoffGoal" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.payoff_goal"}}
        data-test-sf-0726-field="payoffGoal"
        @onChange={{fn (mut this.payoffGoal)}}
        @value={{this.payoffGoal}}
        as |Radio|
      >
        <p class="text-xs mb-3">{{t "strategy_finder_0726.payoff_goal_blurb"}}</p>
        <Radio @value="quick" @label={{t "strategy_finder_0726.payoff_quickly"}} />
        <Radio @value="slow" @label={{t "strategy_finder_0726.lower_monthly"}} />
      </RadioGroup>

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "affordPayments" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.afford_payments"}}
        data-test-sf-0726-field="affordPayments"
        @onChange={{fn (mut this.affordPayments)}}
        @value={{this.affordPayments}}
        as |Radio|
      >
        <p class="text-xs mt-1 mb-3">{{t "strategy_finder_0726.afford_payments_blurb"}}</p>
        <Radio @value="yes" @label={{t "common.yes"}} />
        <Radio @value="no" @label={{t "common.no"}} />
      </RadioGroup>

      {{#if this.showCohortNote}}
        <AlertBanner @intent="info" class="my-2" data-test-sf-0726-nslds-cohort-note>
          <p class="text-sm">
            {{t
              "strategy_finder_0726.nslds_cohort_note"
              generatedDate=this.nsldsGeneratedDateLabel
              firstLoanDate=this.nsldsFirstLoanDateLabel
              lastLoanDate=this.nsldsLastLoanDateLabel
            }}
            {{#if @nsldsUploadRoute}}
              <LinkTo
                @route={{@nsldsUploadRoute}}
                @query={{hash providers="nslds" source="strategy_finder"}}
                class="tio-copy"
                data-test-sf-0726-nslds-cohort-upload-link
              >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
            {{/if}}
          </p>
        </AlertBanner>
      {{/if}}

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "firstLoanTime" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.first_loan_time"}}
        data-test-sf-0726-field="firstLoanTime"
        @onChange={{fn (mut this.firstLoanTime)}}
        @value={{this.firstLoanTime}}
        as |Radio|
      >
        <Radio
          @value="old_borrower"
          @label={{t "strategy_finder_0726.first_loan_time_options.old_borrower"}}
        />
        <Radio
          @value="old_ibr_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.old_ibr_cohort"}}
        />
        <Radio
          @value="new_ibr_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.new_ibr_cohort"}}
        />
        <Radio
          @value="obbb_cohort"
          @label={{t "strategy_finder_0726.first_loan_time_options.obbb_cohort"}}
        />
      </RadioGroup>

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "lastLoanTime" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.last_loan_time"}}
        data-test-sf-0726-field="lastLoanTime"
        @onChange={{fn (mut this.lastLoanTime)}}
        @value={{this.lastLoanTime}}
        as |Radio|
      >
        <Radio
          @value="pre_obbb_cohort"
          @label={{t "strategy_finder_0726.last_loan_time_options.pre_obbb_cohort"}}
        />
        <Radio
          @value="obbb_cohort"
          @label={{t "strategy_finder_0726.last_loan_time_options.obbb_cohort"}}
        />
      </RadioGroup>

      {{#if this.showPslfNote}}
        <AlertBanner @intent="info" class="my-2" data-test-sf-0726-nslds-pslf-note>
          <p class="text-sm">
            {{t
              "strategy_finder_0726.nslds_pslf_note"
              generatedDate=this.nsldsGeneratedDateLabel
              count=this.pslfCount
            }}
            {{#if @nsldsUploadRoute}}
              <LinkTo
                @route={{@nsldsUploadRoute}}
                @query={{hash providers="nslds" source="strategy_finder"}}
                class="tio-copy"
                data-test-sf-0726-nslds-pslf-upload-link
              >{{t "strategy_finder_0726.loans_nslds_link"}}</LinkTo>.
            {{/if}}
          </p>
        </AlertBanner>
      {{/if}}

      <RadioGroup
        data-legacy-input
        class="py-4"
        @errors={{errorsForField "currentlyWorkingToward" schemaErrors=this.displayErrors}}
        @label={{t "strategy_finder_0726.forgiveness.working_toward"}}
        data-test-sf-0726-field="currentlyWorkingToward"
        @onChange={{fn (mut this.currentlyWorkingToward)}}
        @value={{this.currentlyWorkingToward}}
        as |Radio|
      >
        <Radio @value="yes" @label={{t "common.yes"}} />
        {{#if this.showWorkingDetails}}
          <div class="py-2 ml-7" data-test-sf-0726-working-details>
            <div class="pb-2">
              <label for="currentForgivenessProgram" class="form-field-label !text-sm">
                {{t "strategy_finder_0726.forgiveness.which_program"}}
              </label>
              <Select
                @onChange={{this.setProgram}}
                @items={{this.programOptions}}
                @selected={{this.currentForgivenessProgram}}
                @id="currentForgivenessProgram"
                data-test-sf-0726-field="currentForgivenessProgram"
              />
            </div>
            <label for="madePayments" class="form-field-label !text-sm">
              {{t "strategy_finder_0726.forgiveness.how_many_payments"}}
            </label>
            <Input
              @onChange={{this.setMadePayments}}
              type="number"
              @value={{this.madePayments}}
              autocomplete="off"
              min="0"
              id="madePayments"
            />
          </div>
        {{/if}}
        <Radio
          @value="no-and-not-interested"
          @label={{t "strategy_finder_0726.forgiveness.not_interested"}}
        />
        <Radio @value="no-but-want-learn" @label={{t "strategy_finder_0726.forgiveness.not_yet"}} />
      </RadioGroup>

      {{#if this.showEmployment}}
        <div class="py-4" data-test-sf-0726-employment>
          <p class="form-field-label">{{t "strategy_finder_0726.forgiveness.employment_types"}}</p>
          <p class="text-xs mt-1 mb-3">{{t "strategy_finder_0726.forgiveness.employment_blurb"}}</p>

          <Checkbox
            @checked={{this.isGov}}
            @onChange={{this.toggleGov}}
            @label={{t "strategy_finder_0726.forgiveness.gov_label"}}
            data-test-sf-0726-employment-gov
          />
          {{#if this.isGov}}
            <div class="ml-7 my-2">
              <RadioGroup
                data-legacy-input
                class="py-2"
                @errors={{this.govPlanErrors}}
                @label={{t "strategy_finder_0726.forgiveness.gov_how_long"}}
                @onChange={{fn (mut this.planInGov)}}
                @value={{this.planInGov}}
                as |Radio|
              >
                <p class="text-xs mt-1 mb-3">{{t
                    "strategy_finder_0726.forgiveness.pslf_requires"
                  }}</p>
                <Radio
                  @value="for-10-years-more"
                  @label={{t "strategy_finder_0726.forgiveness.at_least_ten"}}
                />
                <Radio
                  @value="not-for-10-years"
                  @label={{t "strategy_finder_0726.forgiveness.ten_years_unlikely"}}
                />
              </RadioGroup>
              <div class="py-2">
                <label for="govMonths" class="form-field-label !text-sm">
                  {{t "strategy_finder_0726.forgiveness.gov_how_many_months"}}
                </label>
                <Input
                  @onChange={{this.setEmploymentMonths}}
                  type="number"
                  @value={{this.employmentMonths}}
                  autocomplete="off"
                  min="0"
                  id="govMonths"
                />
              </div>
            </div>
          {{/if}}

          <Checkbox
            @checked={{this.isTeacher}}
            @onChange={{this.toggleTeacher}}
            @label={{t "strategy_finder_0726.forgiveness.teacher_label"}}
            data-test-sf-0726-employment-teacher
          />
          {{#if this.isTeacher}}
            <div class="ml-7 my-2">
              <AlertBanner @intent="info" class="mb-3">
                <p class="text-sm">{{t "strategy_finder_0726.forgiveness.if_public_school"}}</p>
              </AlertBanner>

              <RadioGroup
                data-legacy-input
                class="py-2"
                @errors={{this.teacherPlanErrors}}
                @label={{t "strategy_finder_0726.forgiveness.teacher_how_long"}}
                @onChange={{fn (mut this.planAsTeacher)}}
                @value={{this.planAsTeacher}}
                as |Radio|
              >
                <p class="text-xs mt-1 mb-3">
                  {{t "strategy_finder_0726.forgiveness.teacher_forgiveness_rec"}}
                </p>
                <Radio
                  @value="for-5-years-and-more"
                  @label={{t "strategy_finder_0726.forgiveness.teacher_five_years"}}
                />
                <Radio
                  @value="less-than-5-years"
                  @label={{t "strategy_finder_0726.forgiveness.teacher_five_years_unlikely"}}
                />
              </RadioGroup>

              <div class="py-2">
                <label for="teacherMonths" class="form-field-label !text-sm">
                  {{t "strategy_finder_0726.forgiveness.payments_since_94"}}
                </label>
                <Input
                  @onChange={{this.setEmploymentMonths}}
                  type="number"
                  @value={{this.employmentMonths}}
                  autocomplete="off"
                  min="0"
                  id="teacherMonths"
                />
              </div>

              <div class="py-2" data-test-sf-0726-tlf17500>
                <p class="text-sm">{{t "strategy_finder_0726.forgiveness.tlf17500_intro"}}</p>
                <ul class="list-disc ml-5 text-sm my-1">
                  <li>{{t "strategy_finder_0726.forgiveness.tlf17500_math"}}</li>
                  <li>{{t "strategy_finder_0726.forgiveness.tlf17500_specialed"}}</li>
                </ul>
                <p class="text-xs mb-2">
                  <a
                    href={{TLF_DOE_URL}}
                    target="_blank"
                    rel="noopener noreferrer"
                    class="tio-copy"
                  >
                    {{t "strategy_finder_0726.tlf_link"}}
                  </a>
                </p>
                <Checkbox
                  @checked={{this.tlfQualifies17500}}
                  @onChange={{this.setTlfQualifies17500}}
                  @label={{t "strategy_finder_0726.forgiveness.tlf17500_checkbox"}}
                />
              </div>
            </div>
          {{/if}}

          <Checkbox
            @checked={{this.isOther}}
            @onChange={{this.toggleOther}}
            @label={{t "strategy_finder_0726.forgiveness.other_label"}}
            data-test-sf-0726-employment-other
          />
        </div>
      {{/if}}

      <div class="pt-2 pb-4">
        <div class="pb-3">
          <Popover @triggerMode="hover">
            <:trigger as |pop|>
              <span {{pop.register}} {{pop.hoverHandlers}}>
                <label for="agi" class="form-field-label !text-sm">
                  {{t "strategy_finder_0726.agi"}}
                </label>
              </span>
            </:trigger>
            <:body>
              {{t "strategy_finder_0726.agi_tooltip" placement="top"}}
            </:body>
          </Popover>
          <Input
            @errors={{errorsForField "agi" schemaErrors=this.displayErrors}}
            @onChange={{this.setAgi}}
            @value={{this.agiDollars}}
            autocomplete="off"
            id="agi"
            {{inputmask
              alias="currency"
              prefix="$"
              unmaskAsNumber=true
              digits="0"
              digitsOptional=false
            }}
          />
        </div>

        <RadioGroup
          data-legacy-input
          class="pb-3"
          @errors={{errorsForField "numberOfAdults" schemaErrors=this.displayErrors}}
          @label={{t "strategy_finder_0726.adults"}}
          data-test-sf-0726-field="adultsChoice"
          @onChange={{fn (mut this.adultsChoice)}}
          @value={{this.adultsChoice}}
          as |Radio|
        >
          <Radio @value="1" @label={{t "strategy_finder_0726.adults_one"}} />
          <Radio @value="2" @label={{t "strategy_finder_0726.adults_two"}} />
        </RadioGroup>

        <div class="pb-3">
          <label for="numberOfChildren" class="form-field-label !text-sm">
            {{t "strategy_finder_0726.children"}}
          </label>
          <Input
            @errors={{errorsForField "numberOfChildren" schemaErrors=this.displayErrors}}
            @onChange={{this.setNumberOfChildren}}
            type="number"
            @value={{this.numberOfChildren}}
            autocomplete="off"
            min="0"
            id="numberOfChildren"
          />
        </div>

        <div>
          <label for="residenceState" class="form-field-label !text-sm">
            {{t "strategy_finder_0726.residence_state"}}
          </label>
          <Select
            @searchable={{true}}
            @errors={{errorsForField "residenceState" schemaErrors=this.displayErrors}}
            @onChange={{this.setStateOfResidence}}
            @items={{this.states}}
            @selected={{this.stateOfResidence}}
            @id="residenceState"
          />
        </div>
      </div>

      {{#if this.showIncompleteAlert}}
        <p class="text-sm tio-fg-danger mb-2" role="alert">
          {{t "strategy_finder_0726.please_complete"}}
        </p>
      {{/if}}
      {{#if this.submitError}}
        <AlertBanner @intent="danger" class="mb-4">
          <p class="text-sm">{{t "strategy_finder_0726.submit_error"}}</p>
        </AlertBanner>
      {{/if}}
      <Button
        @intent="primary"
        @isRunning={{this.submitTask.isRunning}}
        data-test-sf-0726-submit
        {{on "click" this.submitTask.perform}}
      >
        {{t "strategy_finder_0726.see_results"}}
      </Button>
    </form>
  </template>
}
